import { useEffect, useState } from 'react';
import type { KuralFull } from '../data/types';
import { dataUrl } from './useGraphIndex';

const MAX_CACHE = 20;

// LRU on a Map (insertion order = access order after re-insertion).
const cache = new Map<number, KuralFull>();
const pending = new Map<number, Promise<KuralFull>>();

function touch(n: number, k: KuralFull) {
  cache.delete(n);
  cache.set(n, k);
  while (cache.size > MAX_CACHE) {
    const oldest = cache.keys().next().value as number | undefined;
    if (oldest === undefined) break;
    cache.delete(oldest);
  }
}

async function fetchKural(n: number): Promise<KuralFull> {
  const cached = cache.get(n);
  if (cached) {
    touch(n, cached);
    return cached;
  }
  const inflight = pending.get(n);
  if (inflight) return inflight;
  const padded = String(n).padStart(4, '0');
  const p = fetch(dataUrl(`data/kurals/${padded}.json`))
    .then((r) => {
      if (!r.ok) throw new Error(`kural ${n}: HTTP ${r.status}`);
      return r.json() as Promise<KuralFull>;
    })
    .then((k) => {
      touch(n, k);
      pending.delete(n);
      return k;
    })
    .catch((err) => {
      pending.delete(n);
      throw err;
    });
  pending.set(n, p);
  return p;
}

export interface KuralState {
  data: KuralFull | null;
  error: Error | null;
  loading: boolean;
}

export function useKural(n: number | null): KuralState {
  const [state, setState] = useState<KuralState>(() => ({
    data: n != null ? cache.get(n) ?? null : null,
    error: null,
    loading: n != null && !cache.has(n),
  }));

  useEffect(() => {
    if (n == null) {
      setState({ data: null, error: null, loading: false });
      return;
    }
    const cached = cache.get(n);
    if (cached) {
      touch(n, cached);
      setState({ data: cached, error: null, loading: false });
      return;
    }
    let cancelled = false;
    setState({ data: null, error: null, loading: true });
    fetchKural(n)
      .then((data) => {
        if (!cancelled) setState({ data, error: null, loading: false });
      })
      .catch((error: Error) => {
        if (!cancelled) setState({ data: null, error, loading: false });
      });
    return () => {
      cancelled = true;
    };
  }, [n]);

  return state;
}
